import { Col, Row, Typography } from 'antd'
import { Button } from 'components/shared/Button'
import { Loading } from 'components/shared/Loading'
import { numberWithDelimiter } from 'helpers/numberWithDelimiter'
import { urls } from 'helpers/urls'
import { zIndex } from 'helpers/zIndex'
import Link from 'next/link'
import { useTranslation } from 'next-i18next'
import FilterIcon from 'public/icons/filter.svg'
import { useState, VFC } from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'
import { Sticky, StickyContainer } from 'react-sticky'
import styled from 'styled-components'

import { ArtWorkCard } from '../ArtWorkCard'
import { ArtWorkFilterForm } from '../ArtWorkFilterForm'

type Props = {
  artWorks: ArtWorkCardType[]
  isValidatingArtworks?: boolean
  setSizeArtworks: (
    size: number | ((_size: number) => number),
  ) => Promise<any[] | undefined>
  artWorkTotalCount: number
  xs?: boolean
  artists?: ArtistNameType[]
  hasHeader?: boolean
}

export const ArtWorks: VFC<Props> = ({
  artWorks,
  isValidatingArtworks,
  setSizeArtworks,
  artWorkTotalCount,
  xs,
  artists,
  hasHeader = false,
}) => {
  const [isFilterVisible, setIsFilterVisible] = useState(false)
  const { t } = useTranslation()

  return isFilterVisible ? (
    <StyledArtWorkFilterFormWrapper
      xs={xs || false}
      isOpenFilterFormForSp={isFilterVisible}
    >
      <ArtWorkFilterForm
        setIsFilterVisible={setIsFilterVisible}
        xs={xs}
        artists={artists}
      />
    </StyledArtWorkFilterFormWrapper>
  ) : (
    <StickyContainer>
      {xs && (
        <Sticky>
          {({ style, isSticky }) => {
            return (
              <div
                style={{
                  ...style,
                  top: '60px',
                  left: 0,
                  width: '100%',
                  zIndex: 1,
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  background: 'white',
                  paddingTop: hasHeader ? (isSticky ? '60px' : 0) : '12px',
                  paddingBottom: hasHeader ? (isSticky ? '12px' : 0) : '12px',
                  paddingLeft: isSticky ? '16px' : 0,
                  paddingRight: isSticky ? '16px' : 0,
                  borderBottom: isSticky
                    ? '1px solid var(--ax-gray-3, #C4C4C4)'
                    : 'none',
                }}
              >
                <div style={{ display: 'flex' }}>
                  <Button
                    style={{
                      display: 'flex',
                      fontSize: '14px',
                      fontWeight: 400,
                      gap: '4px',
                    }}
                    size="md"
                    type="primary"
                    onClick={() => setIsFilterVisible(true)}
                  >
                    <FilterIcon />
                    {t('フィルター')}
                  </Button>
                </div>

                {/* <SorterSelect setSortValue={setSortValue} width="175px" /> */}
                {/* ダミーdiv SorterSelect実装後に削除 */}
                <div style={{ height: '48px' }} />
              </div>
            )
          }}
        </Sticky>
      )}
      <div
        style={{
          overflow: 'scroll',
          display: 'flex',
          flexDirection: xs ? 'column' : 'row',
          gap: '24px',
        }}
      >
        {!xs && <ArtWorkFilterForm xs={xs} artists={artists} />}
        <div>
          {!xs && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                alignSelf: 'stretch',
                paddingBottom: '28px',
              }}
            >
              <Typography.Text
                style={{ fontSize: '16px', lineHeight: 1, fontWeight: 'bold' }}
              >
                {numberWithDelimiter(artWorkTotalCount)} {t('作品')}
              </Typography.Text>
              {/* <SorterSelect setSortValue={setSortValue} width="238px" /> */}
              {/* ダミーdiv SorterSelect実装後に削除 */}
              <div style={{ height: '48px' }} />
            </div>
          )}

          {xs && (
            <div style={{ padding: '12px 0' }}>
              <Typography.Text
                style={{ fontSize: '16px', lineHeight: 1, fontWeight: 'bold' }}
              >
                {numberWithDelimiter(artWorkTotalCount)} {t('作品')}
              </Typography.Text>
            </div>
          )}

          {isValidatingArtworks && !artWorks ? (
            <div style={{ width: '100%', alignSelf: 'flex-start' }}>
              <Loading />
            </div>
          ) : (
            <InfiniteScroll
              dataLength={artWorks?.length || 0}
              next={() => setSizeArtworks((size) => size + 1)}
              hasMore={artWorks?.length < artWorkTotalCount}
              loader={isValidatingArtworks && <Loading />}
              style={{ overflowX: 'clip' }}
            >
              <Row
                gutter={xs ? 16 : 28}
                style={{ minWidth: xs ? '100%' : '866px' }}
              >
                {[...Array(xs ? 2 : 3)].map((_, i) => {
                  return (
                    <Col
                      span={xs ? 12 : 8}
                      key={`lane-${i}`}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        rowGap: xs ? '24px' : '40px',
                      }}
                    >
                      {artWorks
                        .filter((_artWork, index) => index % (xs ? 2 : 3) === i)
                        .map((artWork) => {
                          return (
                            <Link
                              key={artWork.id}
                              href={urls.art_work(artWork.id)}
                              passHref
                            >
                              <a title={artWork.title || 'NO TITLE'}>
                                <ArtWorkCard artWork={artWork} />
                              </a>
                            </Link>
                          )
                        })}
                    </Col>
                  )
                })}
                {!xs && artWorks.length === 2 && (
                  <Col
                    span={8}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      rowGap: '40px',
                      width: '270px',
                    }}
                  />
                )}
              </Row>
            </InfiniteScroll>
          )}
        </div>
      </div>
    </StickyContainer>
  )
}

const StyledArtWorkFilterFormWrapper = styled.div<{
  xs: boolean
  isOpenFilterFormForSp: boolean
}>`
${(props) =>
  props.xs
    ? `
  width:100%;
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: white;
  overflow: scroll;
  z-index: ${zIndex.header + 1};
  ${!props.isOpenFilterFormForSp ? 'display: none;' : ''}
  `
    : `
    width: 192px;
  `}
}
`
